<script setup lang="ts">
// Static illustration for the "Made for gamers too" landing section, a slice of
// Ava's page with the real setup section (SetupDisplay: categorized items with
// notes and the photo thumbnail) and game tiles with handle chips (GamesDisplay).
withDefaults(defineProps<{
  /** Drop the outer framed panel, for use inside a card that already frames it */
  bare?: boolean
}>(), { bare: false })

const setup = [
  { icon: 'i-lucide-cpu', label: 'PC / Specs', items: [{ name: 'RTX 4080 Super', note: '16GB' }, { name: 'Ryzen 7 7800X3D', note: null }] },
  { icon: 'i-lucide-keyboard', label: 'Keyboard', items: [{ name: 'Custom 65%', note: 'Lubed linears' }] },
  { icon: 'i-lucide-mic', label: 'Microphone', items: [{ name: 'Dynamic mic + arm', note: null }] }
]

const games = [
  { name: 'Valorant', icon: 'i-simple-icons-valorant', color: '#FA4454', handle: 'ava#eu' },
  { name: 'Fortnite', icon: 'i-simple-icons-fortnite', color: null, handle: 'ava_' },
  { name: 'League of Legends', icon: 'i-simple-icons-leagueoflegends', color: '#C28F2C', handle: 'avaplays' }
]
</script>

<template>
  <div
    aria-hidden="true"
    class="flex w-full select-none flex-col pointer-events-none"
    :class="bare ? '' : 'rounded-2xl border border-default bg-elevated/30 p-5 sm:p-6 shadow-(--surface-shadow)'"
  >
    <MockPage
      bg-position="20% 70%"
      class="flex flex-1 flex-col justify-center rounded-xl p-4 font-space-grotesk sm:p-5"
    >
      <div class="mock-card mx-auto w-full max-w-sm px-5 pb-5 pt-4 [zoom:0.9]">
        <!-- Setup -->
        <MockSectionTitle label="My setup" />
        <div class="mt-4 flex gap-4">
          <div class="min-w-0 flex-1 space-y-3 text-(--mock-text-strong)">
            <div
              v-for="group in setup"
              :key="group.label"
            >
              <div class="mb-1.5 flex items-center gap-1.5 opacity-40">
                <UIcon
                  :name="group.icon"
                  class="size-3"
                />
                <span class="text-[10px] uppercase tracking-wider">{{ group.label }}</span>
              </div>
              <div class="space-y-1">
                <div
                  v-for="item in group.items"
                  :key="item.name"
                  class="flex items-baseline gap-2"
                >
                  <span class="inline-flex items-baseline gap-1 text-sm underline decoration-current/30 decoration-dotted underline-offset-4 opacity-80">
                    {{ item.name }}
                    <UIcon
                      name="i-lucide-external-link"
                      class="size-3 shrink-0 self-center opacity-40"
                    />
                  </span>
                  <span
                    v-if="item.note"
                    class="text-xs opacity-40"
                  >{{ item.note }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="relative aspect-5/6 w-1/4 min-w-20 shrink-0 self-start overflow-hidden rounded-lg">
            <MockPhoto name="setup-photo" />
          </div>
        </div>

        <!-- Games -->
        <div class="mt-6">
          <MockSectionTitle label="Games I play" />
        </div>
        <div class="mt-4 flex justify-center gap-2">
          <div
            v-for="game in games"
            :key="game.name"
            class="flex min-h-26 w-24 flex-col items-center gap-2 rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg) p-3"
          >
            <div class="flex size-10 items-center justify-center">
              <UIcon
                :name="game.icon"
                class="size-8"
                :class="game.color ? '' : 'text-(--mock-text-strong)'"
                :style="game.color ? { color: game.color } : undefined"
              />
            </div>
            <p class="line-clamp-2 text-center text-[10px] leading-tight text-(--mock-text-muted)">
              {{ game.name }}
            </p>
            <span class="inline-flex max-w-full items-center gap-1 truncate rounded-full border border-(--mock-card-border) bg-(--mock-tile-bg) px-1.5 py-0.5 text-[9px] text-(--mock-text-muted)">
              <UIcon
                name="i-lucide-copy"
                class="size-2.5 shrink-0 opacity-60"
              />
              {{ game.handle }}
            </span>
          </div>
        </div>
      </div>
    </MockPage>
  </div>
</template>
